import { useState, type ButtonHTMLAttributes } from "react";

export interface CalendarLesson {
  id: string;
  startsAt: string;
  endsAt: string;
  title: string | null;
  tutorName?: string | null;
  location?: string | null;
}

const FAMILY_TIMEZONE = "Europe/London";
const ZONED_INSTANT = /(Z|[+-]\d{2}:\d{2})$/i;
const CALENDAR_DOWNLOAD_ERROR =
  "Could not create a calendar file for this lesson. Refresh the page and try again.";

function calendarText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

function calendarInstant(value: string): string {
  if (!ZONED_INSTANT.test(value)) {
    throw new Error("Calendar events require a timezone-aware date/time.");
  }

  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) {
    throw new Error("Calendar events require a valid date/time.");
  }

  return instant
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

function calendarUid(id: string): string {
  const safe = id.replace(/[^A-Za-z0-9._-]/g, "-");
  return `${safe || "lesson"}@student-hub.wirralmusicfactory`;
}

/**
 * RFC 5545 content lines are limited to 75 octets, not 75 JavaScript
 * characters. Fold only between Unicode code points so a multi-byte UTF-8
 * character is never split. Continuation lines start with one space, which is
 * itself part of the 75-octet physical-line limit.
 */
function foldCalendarLine(line: string): string[] {
  const encoder = new TextEncoder();
  const folded: string[] = [];
  let current = "";
  let currentBytes = 0;

  for (const character of line) {
    const characterBytes = encoder.encode(character).length;
    if (current && currentBytes + characterBytes > 75) {
      folded.push(current);
      current = ` ${character}`;
      currentBytes = 1 + characterBytes;
    } else {
      current += character;
      currentBytes += characterBytes;
    }
  }

  folded.push(current);
  return folded;
}

/**
 * Build a one-off iCalendar event from family-visible lesson data only.
 *
 * DTSTART/DTEND are deliberately emitted as UTC instants. That is the least
 * ambiguous standards-based representation for a browser export: the server's
 * timezone-aware timestamp is preserved exactly and calendar clients convert
 * it to local time themselves, including across BST/GMT changes.
 */
export function calendarLessonIcs(
  lesson: CalendarLesson,
  generatedAt: Date = new Date(),
): string {
  const title = lesson.title?.trim() || "Music lesson";
  const description = [
    lesson.tutorName ? `Tutor: ${lesson.tutorName}` : null,
    "One-off calendar copy from the Student Hub. Check the Hub for later changes or cancellations.",
  ]
    .filter((part): part is string => Boolean(part))
    .join("\n");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Wirral Music Factory//Student Hub//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-TIMEZONE:${FAMILY_TIMEZONE}`,
    "BEGIN:VEVENT",
    `UID:${calendarUid(lesson.id)}`,
    `DTSTAMP:${calendarInstant(generatedAt.toISOString())}`,
    `DTSTART:${calendarInstant(lesson.startsAt)}`,
    `DTEND:${calendarInstant(lesson.endsAt)}`,
    `SUMMARY:${calendarText(title)}`,
    `DESCRIPTION:${calendarText(description)}`,
    ...(lesson.location?.trim() ? [`LOCATION:${calendarText(lesson.location.trim())}`] : []),
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return `${lines.flatMap(foldCalendarLine).join("\r\n")}\r\n`;
}

export function calendarLessonFilename(lesson: CalendarLesson): string {
  const title = (lesson.title?.trim() || "music-lesson")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  const day = lesson.startsAt.slice(0, 10).replace(/[^0-9-]/g, "");
  return `${title || "music-lesson"}${day ? `-${day}` : ""}.ics`;
}

export function downloadCalendarLesson(lesson: CalendarLesson): void {
  const blob = new Blob([calendarLessonIcs(lesson)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = calendarLessonFilename(lesson);
  anchor.style.display = "none";
  document.body.appendChild(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
    // Some browsers have not fully handed the blob to the download subsystem
    // when click() returns. Revoking on the next task keeps the URL alive long
    // enough for Firefox/mobile WebViews without leaving it around afterwards.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export function AddToCalendarButton({
  lesson,
  children = "Add to calendar",
  className = "quiet small",
  ...buttonProps
}: {
  lesson: CalendarLesson;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick">) {
  const [error, setError] = useState<string | null>(null);

  function handleClick() {
    try {
      downloadCalendarLesson(lesson);
      setError(null);
    } catch {
      setError(CALENDAR_DOWNLOAD_ERROR);
    }
  }

  return (
    <>
      <button
        {...buttonProps}
        className={className}
        type="button"
        onClick={handleClick}
      >
        {children}
      </button>
      {error && (
        <span className="small" role="status">
          {error}
        </span>
      )}
    </>
  );
}
